import flatpickr from "flatpickr";
import confirmDatePlugin from "flatpickr/dist/plugins/confirmDate/confirmDate";
import jQuery from "jquery";
import {
    ALLOWED_BUSINESSES,
    POI_TYPE_PRIORITY_BY_BUSINESS,
    POI_TYPE_PRIORITY_DEFAULT,
    TAGS,
    currency as currencyCode,
    statuses,
} from "../data/constants";
import DATA from "../data/data";
import SERVICE_REGISTRY from "../services/registry";
import { SETTINGS } from "../settings";
import { DirectionsProvider } from "../stores-js/location/directions";
import { LatLngBounds } from "../stores-js/maps/geometry";
import { MVCObject } from "../stores-js/mvcobject";
import { SearchParameters } from "../stores-js/searchparameters";
import { SearchView } from "../stores-js/ui/searchview";
import { TableView } from "../stores-js/ui/tableview";
import { iconsprite } from "./iconsprite";
import { initSearchFocus } from "./ui";

const $ = jQuery;
let spriteInjected = false;
let selectedRouteIndex = 0;

export function concatenateStoreHours(openHours) {
    let hoursText = "";
    let end;

    if (openHours.length === 1) {
        const { start } = openHours[0];
        end = openHours[0].end;
        // "all-day" slots carry no start/end; "00:00"-"23:59" is the legacy full-day pair.
        if (openHours[0]["all-day"] === true || (start === "00:00" && end === "23:59")) {
            if (total.maps.L10n.ui.detail.open24) return total.maps.L10n.ui.detail.open24;
            return "24h/24";
        }
    }

    for (const idx in openHours) {
        end = openHours[idx].end;
        if (end === "23:59") {
            end = "00:00";
        }
        hoursText += `${openHours[idx].start} - ${end}`;
        if (idx < openHours.length - 1) {
            hoursText += ", ";
        }
    }

    return hoursText;
}

// Treats null, undefined, the literal strings "null"/"undefined", and blank
// strings as absent so they never reach a rendered address.
export function sanitizeField(value) {
    if (value === null || value === undefined) return "";
    const text = String(value).trim();
    if (text === "" || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") return "";
    return text;
}

// Returns the street lines joined by ", ", guarding a missing address/lines and
// dropping empty/"null" lines. Empty when no usable line exists.
export function formatAddressLines(store) {
    const address = store?.properties?.address;
    if (!address || !Array.isArray(address.lines)) return "";
    return address.lines.map(sanitizeField).filter(Boolean).join(", ");
}

export function concatenateStoreAddress(store) {
    return [formatAddressLines(store), formatZipCityAddress(store)].filter(Boolean).join(", ");
}

export function formatDistanceValue(distanceInMeters) {
    let units;
    let distance = distanceInMeters;

    if (SETTINGS.unitSystem === google.maps.UnitSystem.IMPERIAL) {
        units = "mi";
        distance *= 0.621371;
    } else if (SETTINGS.unitSystem === google.maps.UnitSystem.METRIC) {
        units = "km";
    }

    let roundDistance = Math.round(distance / 100) / 10;
    roundDistance = roundDistance.toString();

    if (SETTINGS.country !== "us") roundDistance = roundDistance.replace(".", ",");

    return roundDistance + units;
}

export function formatStoreDistanceValue(store) {
    if (!store || !store.properties || !store.properties.distance) {
        return "-";
    }

    return formatDistanceValue(store.properties.distance);
}

export function formatZipCityAddress(store) {
    const address = store?.properties?.address;
    if (!address) return "";

    const city = sanitizeField(address.city);
    const zipcode = sanitizeField(address.zipcode);

    if (address.country_code === "US") {
        const userProperties = store.properties.user_properties;
        const state = sanitizeField(userProperties?.state);
        // "City, State Zip" — comma only when something follows the city.
        const cityPart = city && (state || zipcode) ? `${city},` : city;
        return [cityPart, state, zipcode].filter(Boolean).join(" ");
    }

    // "Zip - City"
    return [zipcode, city].filter(Boolean).join(" - ");
}

export function generateHoursLiArray(selectedStore) {
    const usualHours = selectedStore.properties.opening_hours.usual;
    // TO DO: NOT CLEAR ABOUT THE DEFAULT TIMINGS
    // const defaultHourText = concatenateStoreHours(usualHours.default);
    const defaultHourText = concatenateStoreHours([]);

    const openingHoursArray = [];

    function createLiHour(firstDay, lastDay, hourText) {
        const daysText = total.maps.L10n.ui.detail.days;
        let node = null;
        if (hourText === "") {
            // nothing
        } else if (firstDay === lastDay) {
            node = document.createElement("li");
            node.textContent = `${daysText[firstDay].full}: ${hourText}`;
        } else {
            node = document.createElement("li");

            node.textContent = `${daysText[firstDay].short}-${daysText[lastDay].short}: ${hourText}`;
        }

        if (node !== null) {
            openingHoursArray.push(node);
        }
    }

    let firstDaySerie = 0;
    let currentHoursText = "";
    let previousHoursText = "";

    for (let dayIndex = 1; dayIndex < 8; dayIndex++) {
        currentHoursText = "";
        if (dayIndex in usualHours) {
            currentHoursText = concatenateStoreHours(usualHours[dayIndex]);
        } else {
            currentHoursText = defaultHourText;
        }

        if (currentHoursText !== previousHoursText) {
            createLiHour(firstDaySerie, dayIndex - 1, previousHoursText);
            firstDaySerie = dayIndex;
            previousHoursText = currentHoursText;
        }
    }

    createLiHour(firstDaySerie, 7, currentHoursText);

    if (selectedStore.properties.user_properties?.holiday) {
        const special = selectedStore.properties.user_properties.holiday;

        if (special && $.isArray(special) && special.length !== 0) {
            let text = "";
            if (special.length === 0) {
                text = total.maps.L10n.ui.common.closed;
            } else {
                text = concatenateStoreHours(special);
            }

            const node = document.createElement("li");
            node.textContent = `${total.maps.L10n.ui.detail.holiday}: ${text}`;
        }
    }

    return openingHoursArray;
}

export function addBrandIconMarker(element, store) {
    if (!element || !store || !store.properties.types) return;

    if (store.properties.user_properties?.brand) {
        const poiType = store.properties.user_properties?.poiType.toLowerCase() || "fueling";
        if (SETTINGS.iconUrls.brand[store.properties.user_properties.brand]) {
            element.style.backgroundImage = `url('${SETTINGS.iconUrls.brand[store.properties.user_properties.brand].selectedMarker[poiType]}')`;
            element.style.backgroundSize = "64px 64px";
            return;
        }
    }
    element.style.backgroundImage = `url('${SETTINGS.defaultBrandMarkerIcon}')`;
    element.style.backgroundSize = "128px 64px";
    element.style.backgroundPosition = "64px 0px";
}
export function addBrandIconForDetailView(element, store) {
    if (!element || !store || !store.properties.types) return;

    if (store.properties.user_properties?.brand) {
        const poiType = store.properties.user_properties?.poiType.toLowerCase() || "fueling";
        if (SETTINGS.iconUrls.brand[store.properties.user_properties.brand]) {
            element.src =
                SETTINGS.iconUrls.brand[store.properties.user_properties.brand].selectedMarker[poiType] ??
                SETTINGS.defaultBrandMarkerIcon;
            return;
        }
    }
    element.src = `${SETTINGS.defaultBrandMarkerIcon}`;
}

export function addBrandIcon(element, store) {
    if (!element || !store || !store.properties.types) return;

    if (store.properties.user_properties?.brand) {
        const poiType = store.properties.user_properties?.poiType.toLowerCase() || "fueling";
        if (SETTINGS.iconUrls.brand[store.properties.user_properties.brand]) {
            const iconUrl =
                SETTINGS.iconUrls.brand[store.properties.user_properties.brand].icon[poiType] ??
                SETTINGS.defaultBrandIcon;
            element.css({
                "background-image": `url('${iconUrl}')`,
            });
            return;
        }
    }

    element.css({
        "background-image": `url('${SETTINGS.defaultBrandIcon}')`,
    });
}

export function open247(store) {
    const poiType = store?.properties?.user_properties.poiType;
    if (poiType === "WASH") {
        return false;
    }
    return store?.properties?.tags && $(store.properties.tags).filter(TAGS.OPEN_24_7).length !== 0;
}

export function getMoreInfoLink(countryCode, language, poiType, locationID) {
    return `${SETTINGS.totalExternalUrl}/${locationID}?type=${poiType}`;
    /*
    // TO DO: Because we don't know how to manage this criteria for POI finder Apis
    // specific case aviation
    if (SETTINGS.isAviationPortal) {
        return `https://store.totalenergies.fr/${poiId}`;
    }
    if (poiType.toLocaleLowerCase() === "FUELING") {
        let resolvedLanguage = language;

        if (language === "") {
            resolvedLanguage = SETTINGS.languageLink;
        }
        switch (countryCode.toLowerCase()) {
            case "fr":
                return `https://store.totalenergies.fr/fr_FR/${poiId}`;
            case "lu":
                if (language === "de") return `https://store.totalenergies.lu/de_LU/${poiId}`;
                return `https://store.totalenergies/fr_LU/${poiId}`;
            case "be":
                if (language === "du") return `https://store.totalenergies.be/du_BE/${poiId}`;
                if (language === "nl") return `https://store.totalenergies.be/nl_BE/${poiId}`;
                return `https://store.totalenergies.be/fr_BE/${poiId}`;
            case "nl":
                return `https://store.totalenergies.nl/nl_NL/${poiId}`;
            case "de":
                return `https://store.totalenergies.de/de_DE/${poiId}`;
            default:
                return null;
        }
    }
    return null;
    */
}

export class NameTableView extends TableView {
    _add_places() {
        let predictions;
        let $rendered_card;
        predictions = this.get("predictions");

        if (predictions) {
            for (const prediction in predictions) {
                this.tabIndex++;
                if (Object.hasOwn(predictions, prediction)) {
                    ((predictionData) => {
                        $rendered_card = $(this._places_card_renderer.render(predictionData));
                        const self = this;
                        const selectStore = () => {
                            SERVICE_REGISTRY.storeService.getStoreByIdOrCode(predictionData.store_id, (store) => {
                                if (!store) return;
                                const [lng, lat] = store.geometry.coordinates;
                                self.set("highlighted_card_index", -1);
                                self.set("location", { lat, lng });
                                self.set("details", {
                                    location: {
                                        description: store.properties.name,
                                        lat,
                                        lng,
                                    },
                                    viewpoint: {
                                        bounds: { east: lng, north: lat, south: lat, west: lng },
                                    },
                                    store,
                                });
                            });
                        };
                        $rendered_card.on("click", (event) => {
                            event.stopPropagation();
                            selectStore();
                        });
                        $rendered_card.on("keydown", (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                selectStore();
                            }
                        });

                        this.$cards.append($rendered_card);
                    })(predictions[prediction]);
                }
            }
            this.$cards.find(".woosmap-tableview-cell").first().css("border-top", "2px solid #707173");
        }
    }
}

class LocalitiesTableView extends TableView {
    _add_places() {
        let predictions;
        let $rendered_card;
        predictions = this.get("predictions");
        if (predictions) {
            for (const prediction in predictions) {
                this.tabIndex++;
                if (Object.hasOwn(predictions, prediction)) {
                    ((predictionData) => {
                        $rendered_card = $(this._places_card_renderer.render(predictionData));
                        const self = this;
                        const autocompleteCallback = () => {
                            DATA.woosAutocompleteDetails(predictionData.public_id, (results) => {
                                self.set("predictions", []);
                                self.set("highlighted_card_index", -1);
                                self.set("location", {
                                    lat: results.geometry.location.lat,
                                    lng: results.geometry.location.lng,
                                });
                                self.set("details", {
                                    location: {
                                        description: results.name,
                                        lat: results.geometry.location.lat,
                                        lng: results.geometry.location.lng,
                                    },
                                    viewpoint: {
                                        bounds: results.geometry.viewport
                                            ? new LatLngBounds(
                                                  results.geometry.viewport.northeast,
                                                  results.geometry.viewport.southwest,
                                              ).toJSON()
                                            : undefined,
                                    },
                                });
                            });
                        };
                        $rendered_card.on("click", (event) => {
                            autocompleteCallback();
                        });
                        $rendered_card.on("keydown", (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                autocompleteCallback();
                            }
                        });
                        this.$cards.append($rendered_card);
                    })(predictions[prediction]);
                }
            }
            // this.$cards.find(".woosmap-tableview-cell").first().css("border-top", "2px solid #707173");
        }
    }
}

export class WoosmapDirectionsProvider extends DirectionsProvider {
    getDirectionsFromOrToStore() {
        this.request = {
            origin: this.get("originDestination").origin,
            destination: this.get("originDestination").destination,
            travelMode:
                this.get("selectedTravelMode") === "TRANSIT"
                    ? "transit"
                    : google.maps.TravelMode[this.get("selectedTravelMode")],
        };
        SERVICE_REGISTRY.itinaryService.getWoosmapDirections(this.request, (response, status, renderer) => {
            if (status === "OK") {
                // e.setDirectionsObjectsWithResponse(response);
                // this.get("selectedTravelMode") === 'TRANSIT' ? setTransitDirectionsObjectsWithResponse(response, e) : setDirectionsObjectsWithResponse(response, e);
                setDirectionsObjectsWithResponse(response, this, renderer);
            } else {
                console.error("woosmap directions services errors: ", status);
                this.set("directionsSummaries", null);
            }
        });
    }

    cleanMapFromRoutes() {
        for (const provider of this.get("directionsRenderers") ?? []) {
            provider.map = null;
            provider.panel = null;
        }
    }
}

export function setDirectionsObjectsWithResponse(response, e, renderer) {
    const routeSummaries = [];
    const renderers = [];
    let distanceTotal = 0;
    // TO DO - OBSERVE THIS
    // cleanMapFromRoutes(e);
    const selectedTravelMode = e.get("selectedTravelMode");
    if (selectedTravelMode === "TRANSIT") {
        response.routes.map((route, index) => {
            distanceTotal = route.legs.reduce((total, leg) => total + leg.distance, 0);
        });
    }
    response.routes.forEach((route, index) => {
        if (selectedTravelMode === "TRANSIT") {
        }
        const summaryRoute = {
            rendererIndex: index,
            travelMode:
                selectedTravelMode === "TRANSIT" ? "transit" : google.maps.TravelMode[e.get("selectedTravelMode")],
            summary:
                selectedTravelMode === "TRANSIT"
                    ? ""
                    : route?.main_route_name
                      ? route.main_route_name
                      : total?.maps?.L10n?.ui?.directions?.fastest_route ?? "Fastest route",
            distance:
                selectedTravelMode === "TRANSIT" ? formatDistanceValue(distanceTotal) : route.legs[0].distance.text,
            durationLong:
                selectedTravelMode === "TRANSIT" ? formatDuration(route.duration) : route.legs[0].duration.text,
            durationShort:
                selectedTravelMode === "TRANSIT"
                    ? formatDuration(route.duration)
                    : formatDuration(route.legs[0].duration.value),
        };
        routeSummaries.push(summaryRoute);
        renderers.push({
            map: null,
            directions: {
                routes: [route],
                status: e.status,
                request: e.request,
            },
            summary: {
                distance:
                    selectedTravelMode === "TRANSIT" ? formatDistanceValue(distanceTotal) : route.legs[0].distance.text,
                durationLong:
                    selectedTravelMode === "TRANSIT" ? formatDuration(route.duration) : route.legs[0].duration.text,
            },
            routeIndex: index,
            options: null,
            panel: null,
            renderer,
        });
    });
    e.set("directionsSummaries", routeSummaries);
    e.set("directionsRenderers", renderers);
    e.set("directionsLink", e.getDirectionsLink());
}

export function cleanMapFromRoutes(directionsProvider) {
    for (const renderer of directionsProvider.get("directionsRenderers") ?? []) {
        if (renderer) {
            //renderer.map = null;
            renderer.renderer.setMap(null);
        }
        // provider.map = null;
        // provider.panel = null;
    }
}

/* eslint-enable */
WoosmapSearchSource.prototype = new MVCObject();
WoosmapLocalitiesAutoComplete.prototype = new MVCObject();

export function WoosmapLocalitiesAutoComplete(mainElement, suggestionContainerClass) {
    this.autocomplete_query = null;
    this.predictions = null;
    this.detailsPlaces = null;
    this.detailsWoosmapName = null;
    this.mainElement = mainElement;
    this.suggestionContainerClass = suggestionContainerClass;
}
WoosmapLocalitiesAutoComplete.prototype.autocomplete_query_changed = function () {
    const query = this.get("autocomplete_query");
    if (!query || query.length < 3) {
        this.set("predictions", []);
        return;
    }
    DATA.autocomplete(query, (predictions) => {
        this.set("predictions", predictions ?? []);
    });
};
// WoosmapLocalitiesAutoComplete.prototype.predictions_changed = function () {
//   if (predictions === null || predictions.length == 0) {
//     // this.mainElement.find('.' + this.suggestionContainerClass).hide();
//   } else {
//     this.mainElement.find(`.${this.suggestionContainerClass}`).show();
//   }
// };
WoosmapLocalitiesAutoComplete.prototype.detailsPlaces_changed = function () {
    const details = this.get("detailsPlaces");
    this.mainElement.find(`.${this.suggestionContainerClass}`).hide();
};

export function WoosmapSearchSource(mainElement, suggestionContainerClass) {
    this.autocomplete_query = null;
    this.predictions = null;
    this.detailsPlaces = null;
    this.detailsWoosmapName = null;
    this.mainElement = mainElement;
    this.suggestionContainerClass = suggestionContainerClass;
}

WoosmapSearchSource.prototype.detailsPlaces_changed = function () {
    const details = this.get("detailsPlaces");
    this.mainElement.find(`.${this.suggestionContainerClass}`).hide();
};
WoosmapSearchSource.prototype.detailsWoosmapName_changed = function () {
    const details = this.get("detailsWoosmapName");
    this.mainElement.find(`.${this.suggestionContainerClass}`).hide();
};
WoosmapSearchSource.prototype.predictions_changed = function () {
    const predictions = this.get("predictions");
    if (predictions === null || predictions.length === 0) {
        // this.mainElement.find('.' + this.suggestionContainerClass).hide();
    } else {
        this.mainElement.find(`.${this.suggestionContainerClass}`).show();
    }
};
// Retained as a rollback path for autocompleteStoresByText.
// Remove after the new flow has soaked in prod for a while and is working well.
function dedupStoresByNameAndPriority(stores) {
    const selectedBusiness = window.total?.selectedBusiness ?? "all";
    const priorityMap = POI_TYPE_PRIORITY_BY_BUSINESS[selectedBusiness] ?? POI_TYPE_PRIORITY_BY_BUSINESS.all;
    const bestByName = new Map();
    for (const store of stores) {
        const name = store.properties?.name?.toLowerCase();
        if (!name) continue;
        const poiType = store.properties?.user_properties?.poiType?.toUpperCase();
        const priority = priorityMap[poiType] ?? POI_TYPE_PRIORITY_DEFAULT;
        const current = bestByName.get(name);
        if (!current || priority < current.priority) {
            bestByName.set(name, { store, priority });
        }
    }
    return Array.from(bestByName.values()).map((entry) => entry.store);
}

WoosmapSearchSource.prototype.autocomplete_query_changed = function () {
    const query = this.get("autocomplete_query");
    if (query != null && query.length >= 3) {
        SERVICE_REGISTRY.storeService.autocompleteStoresByText(query, (predictions) => {
            this.set("predictions", predictions);
        });
    }
};

export function createAutocompleteInput(
    mainElement,
    initialBounds,
    template,
    containerId,
    suggestionContainerClass,
    onLocationChangeCallback,
    autocompleteByName,
) {
    const googlePlaceAutocompleteOptions = {
        bounds: initialBounds,
        types: ["geocode"],
    };

    const woosmapSearchView = new SearchView(template.render(total.maps.L10n.ui), "<span>{{name}}</span>");
    mainElement.find(`#${containerId}`).html(woosmapSearchView.getContainer());

    initSearchFocus();
    // const woosmapPlacesSearchSource = new woosmap.location.PlacesSearchSource(googlePlaceAutocompleteOptions, 3);
    const woosmapLocalitiesAutoComplete = new WoosmapLocalitiesAutoComplete(mainElement, suggestionContainerClass);
    // const woosmapSugestionTableView = new woosmap.ui.TableView({
    //   cell_store: '<div></div>',
    //   cell_place: '<div class="storemap-suggestion-item">{{description}}</div>',
    // });
    const ariaLabel = total.maps.L10n.ui.accessibility.autocomplete_select_result;

    const woosmapSugestionTableView = new LocalitiesTableView({
        cell_store: "<div></div>",
        cell_place: `<li class="wm-autocomplete--item" data-id="autocomplete-item" tabindex="3" role="button" aria-label="${ariaLabel} {{description}}" data-focus-after="card-detail-link">{{description}}</li>`,
        // cell_place: '<div class="storemap-suggestion-item">{{description}}</div>',
    });

    woosmapSugestionTableView.bindTo("predictions", woosmapLocalitiesAutoComplete);

    woosmapLocalitiesAutoComplete.predictions_changed = function () {
        const predictions = this.get("predictions");
        const hasPredictions = predictions !== null && predictions.length > 0;
        // Keep the dropdown open when a "search by id/code" result is showing — otherwise an
        // empty locality response (e.g. an airport code that is not a place) would race in and
        // hide the container right after the code result appeared.
        const storeByIdVisible = mainElement.find(".storemap-suggestionbar-searchstorebyid-result").is(":visible");
        if (hasPredictions || storeByIdVisible) {
            mainElement.find(`.${suggestionContainerClass}`).show();
        } else {
            mainElement.find(`.${suggestionContainerClass}`).hide();
        }
    };

    woosmapSugestionTableView.details_changed = onLocationChangeCallback;

    // woosmapPlacesSearchSource.bindTo('autocomplete_query', woosmapSearchView, false);
    woosmapLocalitiesAutoComplete.bindTo("autocomplete_query", woosmapSearchView, false);
    woosmapLocalitiesAutoComplete.bindTo("detailsPlaces", woosmapSugestionTableView, "details", false);
    mainElement.find(`.${suggestionContainerClass}`).append(woosmapSugestionTableView.getContainer());
    if (autocompleteByName) {
        const nameTableView = new NameTableView({
            cell_store: "<div></div>",
            cell_place: `<li class="wm-autocomplete--item" data-id="autocomplete-item" tabindex="3" role="button" aria-label="${ariaLabel} {{name}}" data-focus-after="card-detail-link">{{name}}</li>`,
        });

        const woosmapSearchSource = new WoosmapSearchSource(mainElement, suggestionContainerClass);
        woosmapSearchSource.bindTo("autocomplete_query", woosmapSearchView, false);
        woosmapSearchSource.bindTo("detailsPlaces", woosmapSugestionTableView, "details", false);
        woosmapSearchSource.bindTo("detailsWoosmapName", nameTableView, "details", false);
        nameTableView.bindTo("predictions", woosmapSearchSource);
        nameTableView.details_changed = onLocationChangeCallback;
        mainElement.find(`.${suggestionContainerClass}`).append(nameTableView.getContainer());
    }
}

export function createWoosmapDirectionsProvider(itineraryRendererOptions, directionOptions) {
    const woosmapDirectionsProvider = new WoosmapDirectionsProvider(itineraryRendererOptions, directionOptions);
    woosmapDirectionsProvider.selectedTravelMode = "DRIVING";
    return woosmapDirectionsProvider;
}

export function createWoosmapLocalitiesAutoComplete(mainElement, suggestionContainerClass) {
    const woosmapLocalitiesAutoComplete = new WoosmapLocalitiesAutoComplete(mainElement, suggestionContainerClass);
    return woosmapLocalitiesAutoComplete;
}

export function createLocalitiesTableView() {
    const ariaLabel = total.maps.L10n.ui.accessibility.autocomplete_select_result;
    const woosmapSugestionTableView = new LocalitiesTableView({
        cell_store: "<div></div>",
        cell_place: `<li class="wm-autocomplete--item" data-id="autocomplete-item" tabindex="3" role="button" aria-label="${ariaLabel} {{description}}" data-focus-after="card-detail-link">{{description}}</li>`,
        // cell_place: '<div class="storemap-suggestion-item">{{description}}</div>',
    });
    return woosmapSugestionTableView;
}
// Fraction of icon size from center where the off-center pin tails sit.
// Tune visually if WASH / EV tails don't land on the lat/lng.
const PIN_TAIL_OFFSET = 0.4;

function anchorXYSquare(poiType, size) {
    if (poiType === "FUELING" || poiType === "DISTRIBUTOR") return [0, 0];
    if (poiType === "WASH" || poiType === "GARAGE") return [size, 0];
    if (poiType === "AIRPORT" || poiType === "AIRFIELD") return [size / 2, size / 2];
    return [size, size]; // EV and others
}

function anchorXYPin(poiType, size) {
    const cx = size / 2;
    const dx = size * PIN_TAIL_OFFSET;
    if (poiType === "WASH" || poiType === "GARAGE") return [cx + dx, size]; // bottom-right
    if (poiType === "EV") return [cx + dx, 0]; // top-right
    return [cx - dx, size]; // FUELING, DISTRIBUTOR, default — bottom-left
}

// Switch between artwork variants:
//   anchorXYSquare — corner-anchored (works for legacy squares and plain circles)
//   anchorXYPin    — tail-tip anchored (for circle-with-tail pin artwork)
export const anchorXY = anchorXYSquare;

function getAnchorPoint(poiType, size) {
    const [x, y] = anchorXY(poiType, size);
    return new google.maps.Point(x, y);
}

// The SDK loads marker icons with crossOrigin (it uploads them as WebGL textures).
// The icon host only returns Access-Control-Allow-Origin when a request carries an
// Origin header, and sends no Vary on the response when it doesn't — so a plain
// <img>/CSS background load of the same URL (results list, detail card) caches a
// header-less response that the CORS load then reuses and rejects, leaving markers
// blank. Tagging the CORS variant keeps the two on separate cache entries.
export function corsIconUrl(url) {
    if (!url) return url;
    return `${url}${url.includes("?") ? "&" : "?"}wm=cors`;
}
// Generate markers rules for tiled view on desktop and mobile mapView
export function generateMarkersStyleRules() {
    const styleRules = [];
    let brand;
    let poiType;
    let styleRule;
    let idx;
    let typeIdx;
    let brandTypes;
    let statusIdx;
    let status;
    const reversepoiTypes = [...total.maps.parameters.type].reverse();

    for (statusIdx in statuses) {
        status = statuses[statusIdx];
        for (idx in total.maps.parameters.brand) {
            brand = total.maps.parameters.brand[idx];
            for (typeIdx in reversepoiTypes) {
                poiType = reversepoiTypes[typeIdx];
                // EV charging stations exist only under the TOTAL brand
                if (poiType === "EV" && brand !== "TOTAL") continue;
                brandTypes = `${status}-${brand}-${poiType}`;
                if (SETTINGS.iconUrls.brand[brand]) {
                    const brandIcons = SETTINGS.iconUrls.brand[brand];
                    const poiTypeLower = poiType.toLowerCase();
                    styleRule = {
                        color: "#285AFF",
                        minSize: SETTINGS.simpleRendererMinPointSize,
                        size: SETTINGS.simpleRendererPointSize,
                        type: brandTypes,
                        icon: {
                            url: corsIconUrl(brandIcons.marker[poiTypeLower] ?? SETTINGS.defaultBrandMarkerIcon),
                            anchor: getAnchorPoint(poiType, 40),
                            scaledSize: new google.maps.Size(40, 40),
                        },
                        selectedIcon: {
                            url: corsIconUrl(
                                brandIcons.selectedMarker[poiTypeLower] ?? SETTINGS.defaultBrandMarkerIcon,
                            ),
                            anchor: getAnchorPoint(poiType, 60),
                            scaledSize: new google.maps.Size(60, 60),
                        },
                    };
                } else {
                    styleRule = {
                        color: "#285AFF",
                        minSize: SETTINGS.simpleRendererMinPointSize,
                        size: SETTINGS.simpleRendererPointSize,
                        type: brandTypes,
                        icon: {
                            url: corsIconUrl(SETTINGS.defaultBrandMarkerIcon),
                            anchor: getAnchorPoint(poiType, 40),
                            scaledSize: new google.maps.Size(40, 40),
                        },
                        selectedIcon: {
                            url: corsIconUrl(SETTINGS.defaultBrandMarkerIcon),
                            anchor: getAnchorPoint(poiType, 60),
                            scaledSize: new google.maps.Size(60, 60),
                        },
                    };
                }
                styleRules.push(styleRule);
            }
        }
    }
    return {
        breakPoint: SETTINGS.rendererBreakScale,
        rules: styleRules,
        default: {
            color: "#285AFF",
            minSize: SETTINGS.simpleRendererMinPointSize,
            size: SETTINGS.simpleRendererPointSize,
            icon: {
                url: corsIconUrl(SETTINGS.defaultBrandMarkerIcon),
                anchor: new google.maps.Point(20, 40),
                scaledSize: new google.maps.Size(80, 40), // Taille à l'échelle du sprite
            },
            selectedIcon: {
                url: corsIconUrl(SETTINGS.defaultBrandMarkerIcon),
                anchor: new google.maps.Point(30, 60),
                scaledSize: new google.maps.Size(60, 60), // Taile de l'icone
            },
        },
    };
}

export const directionsStepsIcon = [
    {
        iconIdx: 1,
        key: "straight",
        icon: "https://images.woosmap.com/directions/straight_black.png",
    },
    {
        iconIdx: 2,
        key: "turn-slight-right",
        icon: "https://images.woosmap.com/directions/turn_slight_left_black.png",
    },
    {
        iconIdx: 3,
        key: "turn-slight-left",
        icon: "https://images.woosmap.com/directions/turn_slight_left_black.png",
    },
    {
        iconIdx: 4,
        key: "straight",
        icon: "https://images.woosmap.com/directions/straight_black.png",
    },
    {
        iconIdx: 5,
        key: "turn-slight-right",
        icon: "https://images.woosmap.com/directions/turn_slight_left_black.png",
    },
    {
        iconIdx: 6,
        key: "turn-slight-left",
        icon: "https://images.woosmap.com/directions/turn_slight_left_black.png",
    },
    {
        iconIdx: 8,
        key: "straight",
        icon: "https://images.woosmap.com/directions/straight_black.png",
    },
    {
        iconIdx: 9,
        key: "turn-slight-right",
        icon: "https://images.woosmap.com/directions/turn_slight_left_black.png",
    },
    {
        iconIdx: 10,
        key: "turn-right",
        icon: "https://images.woosmap.com/directions/turn_left_black.png",
    },
    {
        iconIdx: 11,
        key: "turn-sharp-right",
        icon: "https://images.woosmap.com/directions/turn_left_black.png",
    },
    {
        iconIdx: 12,
        key: "uturn-right",
        icon: "https://images.woosmap.com/directions/turn_left_black.png",
    },
    {
        iconIdx: 13,
        key: "uturn-left",
        icon: "https://images.woosmap.com/directions/turn_left_black.png",
    },
    {
        iconIdx: 14,
        key: "turn-sharp-left",
        icon: "https://images.woosmap.com/directions/turn_left_black.png",
    },
    {
        iconIdx: 15,
        key: "turn-left",
        icon: "https://images.woosmap.com/directions/turn_left_black.png",
    },
    {
        iconIdx: 16,
        key: "turn-slight-left",
        icon: "https://images.woosmap.com/directions/turn_slight_left_black.png",
    },
    {
        iconIdx: 17,
        key: "straight",
        icon: "https://images.woosmap.com/directions/straight_black.png",
    },
    {
        iconIdx: 18,
        key: "ramp-right",
        icon: "https://images.woosmap.com/directions/merge_slight_left_black.png",
    },
    {
        iconIdx: 19,
        key: "ramp-left",
        icon: "https://images.woosmap.com/directions/merge_slight_left_black.png",
    },
    {
        iconIdx: 20,
        key: "ExitRight",
        icon: "https://images.woosmap.com/directions/merge_left_black.png",
    },
    {
        iconIdx: 21,
        key: "ExitLeft",
        icon: "https://images.woosmap.com/directions/merge_left_black.png",
    },
    {
        iconIdx: 22,
        key: "straight",
        icon: "https://images.woosmap.com/directions/straight_black.png",
    },
    {
        iconIdx: 23,
        key: "fork-right",
        icon: "https://images.woosmap.com/directions/merge_slight_left_black.png",
    },
    {
        iconIdx: 24,
        key: "fork-left",
        icon: "https://images.woosmap.com/directions/merge_slight_left_black.png",
    },
    {
        iconIdx: 25,
        key: "merge",
        icon: "https://images.woosmap.com/directions/merge_black.png",
    },
    {
        iconIdx: 26,
        key: "roundabout-left",
        icon: "https://images.woosmap.com/directions/roundabout_black.png",
    },
    {
        iconIdx: 27,
        key: "roundabout-right",
        icon: "https://images.woosmap.com/directions/roundabout_black.png",
    },
    {
        iconIdx: 28,
        key: "ferry",
        icon: "https://images.woosmap.com/directions/boat_black.png",
    },
    {
        iconIdx: 29,
        key: "ferry",
        icon: "https://images.woosmap.com/directions/boat_black.png",
    },
    {
        iconIdx: 37,
        key: "fork-right",
        icon: "https://images.woosmap.com/directions/merge_left_black.png",
    },
    {
        iconIdx: 38,
        key: "fork-left",
        icon: "https://images.woosmap.com/directions/merge_left_black.png",
    },
];

export const Converter = ((undefined) => {
    const _overlay = new google.maps.OverlayView();
    _overlay.draw = () => {};
    _overlay.remove = () => {};

    const setMap = (map) => {
        if (map !== null) {
            _overlay.setMap(map);
        }
    };

    const fromLatLngToDivPixel = (location) => _overlay.getProjection().fromLatLngToContainerPixel(location);

    const fromDivPixelToLatLng = (point) => _overlay.getProjection().fromContainerPixelToLatLng(point);

    const fromLatLngToPoint = (location) => _overlay.getProjection().fromLatLngToPoint(location);

    return {
        setMap,
        fromLatLngToDivPixel,
        fromDivPixelToLatLng,
        fromLatLngToPoint,
    };
})();

export const EventManager = ((undefined) => {
    const listeners = {};

    const add = (event, func, scope, context) => {
        if (!listeners[event]) listeners[event] = [];
        const _scope = scope || window;
        listeners[event].push({
            func,
            scope: _scope,
            context: context || scope,
        });
    };

    const notify = (event, data, context) => {
        if (!listeners[event]) return;
        let listener;
        for (let index = 0, { length } = listeners[event]; index < length; index++) {
            listener = listeners[event][index];
            if (typeof context !== "undefined" && context !== listener.context) continue;
            // Before error handling was added, a listener throwing an exception would stop the loop and prevent other listeners from being called
            try {
                if (listener.func.call(listener.scope, data) === false) return false;
            } catch (error) {
                console.error(`EventManager listener for "${event}" failed:`, error);
            }
        }
        return true;
    };

    return {
        add,
        notify,
    };
})();

export function formatDuration(seconds) {
    const secondsNum = Number.parseInt(seconds);
    const hours = Math.floor(secondsNum / 3600);
    const minutes = Math.round((secondsNum - 3600 * hours) / 60);
    let result = "";
    if (hours > 0) {
        result += `${hours.toString()} h`;
        if (minutes > 0) {
            result += " ";
        }
    }
    if (minutes > 0) {
        result += `${minutes.toString()} min`;
    }
    return result;
}

export function getTransitMode(mode) {
    const transitModes = {
        train: [
            "highSpeedTrain",
            "intercityTrain",
            "interRegionalTrain",
            "regionalTrain",
            "cityTrain",
            "subway",
            "monorail",
            "inclined",
        ],
        tram: ["lightRail"],
        bus: ["bus", "privateBus", "busRapid"],
        ferries: ["ferry"],
        aerial: ["aerial", "flight", "spaceship"],
    };
    const transitKey = Object.keys(transitModes).find((k) => transitModes[k].includes(mode));
    return transitKey;
}
export function getProductImageUrl(store, tag) {
    const productInfo = total.maps.L10n.products[tag];
    if (productInfo) {
        return productInfo.pictogram_url === null ? SETTINGS.defaultBrandIcon : productInfo.pictogram_url;
    }
}
// TO DO: Mock data for local env. Hot Data Api endpoint works only for *.woosmap.com
export function generateMockPrice(min, max) {
    const randomPrice = (Math.random() * (max - min) + min).toFixed(2);
    return Number.parseFloat(randomPrice);
}
export function getProductName(store, tag) {
    const productInfo = total.maps.L10n.products[tag];
    if (productInfo) {
        return productInfo.name === null ? tag : productInfo.name;
    }
}
export function getProductHotData(products, tag) {
    if (products.length > 0) {
        const filteredData = products.filter((product) => product.product_code === tag);
        if (filteredData && filteredData.length > 0) {
            const rawStatus = filteredData[0].availability_status;
            const availabilityStatus = rawStatus == null ? "not_applicable" : rawStatus.toString().toLowerCase();
            const refinedData = {
                value: filteredData[0].price,
                currency: currencyCode[filteredData[0].currency_code],
                isNA: availabilityStatus === "not_applicable",
                [availabilityStatus]: true,
                updateDate: filteredData[0].price_update_date_time,
            };
            return refinedData;
        }
    }
}
export function formatDateByCountry(dateText) {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const options = {
        timeZone: timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
    };
    const locale = SETTINGS.country === "fr" ? "fr-FR" : "en-GB";
    const dateParts = new Date(dateText).toLocaleString(locale, options).split(" ");

    const [day, month, year] = dateParts[0].replace(",", "").split("/");
    const timePart = dateParts[1];

    //TO DO: Temporarily hide the time part till POI finder APIs fixes.
    return `${year}-${month}-${day}`;
}
export function generateCategoryWiseQuery(data, fieldName) {
    const q = woosmap.map.query;
    const tagsGroupedByCategory = {};
    for (const item of data) {
        if (!tagsGroupedByCategory[item.category]) {
            tagsGroupedByCategory[item.category] = [];
        }
        tagsGroupedByCategory[item.category].push(item.value);
    }
    const betweenCategoriesQuery = [];
    for (const [category, values] of Object.entries(tagsGroupedByCategory)) {
        const fields = values.map((value) => q.F(fieldName, value));
        const withinCategoryQuery = new woosmap.map.query.Query(fields, "OR"); // Within-category operator
        betweenCategoriesQuery.push(withinCategoryQuery);
    }
    if (betweenCategoriesQuery.length > 0) {
        const finalQuery = new woosmap.map.query.Query(betweenCategoriesQuery, "AND"); // Between-category operator
        return finalQuery;
    }
}
export function generateSearchQuery() {
    function generateQuery(data, fieldName, operator) {
        const q = woosmap.map.query;
        const fields = [];
        if (data.length > 0) {
            for (const item of data) {
                fields.push(q.F(fieldName, item.value));
            }
            return new woosmap.map.query.Query(fields, operator);
        }
    }

    const q = woosmap.map.query;
    const tagQuery = generateCategoryWiseQuery(SERVICE_REGISTRY.queryService.getTagArray(), "tag");
    const typeQuery = generateQuery(SERVICE_REGISTRY.queryService.getTypeArray(), "type", "OR");
    const searchParameters = new SearchParameters();

    let combinedQuery = null;
    if (tagQuery && typeQuery) combinedQuery = q.and([tagQuery, typeQuery]);
    else if (tagQuery) combinedQuery = tagQuery;
    else if (typeQuery) combinedQuery = typeQuery;

    const selectedBusiness = window.total?.selectedBusiness ?? "all";
    const selectedCountry = window.total?.selectedCountry ?? null;

    const filters = [];
    const businesses = selectedBusiness
        .split(",")
        .map((b) => b.trim())
        .filter((b) => ALLOWED_BUSINESSES.includes(b));

    if (businesses.length === 1) {
        filters.push(q.F("user.business", businesses[0]));
    } else if (businesses.length > 1) {
        filters.push(q.or(businesses.map((b) => q.F("user.business", b))));
    }
    if (selectedCountry) {
        filters.push(q.F("user.country", selectedCountry));
    }

    if (filters.length) {
        if (combinedQuery) {
            combinedQuery = q.and([combinedQuery, ...filters]);
        } else {
            combinedQuery = filters.length === 1 ? filters[0] : new woosmap.map.query.Query(filters, "AND");
        }
    }

    if (combinedQuery) searchParameters.query = combinedQuery;
    return searchParameters.query;
}

export function getPaddingForWindow() {
    const fitBoundsOptions = window.innerWidth < 768 ? SETTINGS.mobilePaddingBounds : SETTINGS.paddingBounds;
    return fitBoundsOptions;
}
export function onItinaryDateChange(selectedDate) {
    const currentDate = new Date();

    if (currentDate > selectedDate) {
        const departureTime = Math.floor(currentDate.getTime() / 1000);
        SERVICE_REGISTRY.itinaryService.setStartDatetime(departureTime);
    } else {
        const departureTime = Math.floor(selectedDate.getTime() / 1000);
        SERVICE_REGISTRY.itinaryService.setStartDatetime(departureTime);
    }
}
export function injectSprite(svgSprite) {
    if (spriteInjected) return;
    spriteInjected = true;

    const container = document.createElement("div");
    container.innerHTML = svgSprite.trim();
    document.body.insertBefore(container.firstChild, document.body.firstChild);
}
export function initIcons() {
    injectSprite(iconsprite);
    const iconElements = document.querySelectorAll("[data-icon]");

    for (const element of iconElements) {
        const iconName = element.getAttribute("data-icon");
        const size = element.getAttribute("data-size") || 28;
        const color = element.getAttribute("data-color") || "#E70000";
        const strokeWidth = element.getAttribute("data-stroke-width") || 1.7;

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("viewBox", "0 0 24 24");
        svg.setAttribute("width", size);
        svg.setAttribute("height", size);
        svg.setAttribute("stroke", color);
        svg.setAttribute("stroke-width", strokeWidth);
        svg.setAttribute("fill", "none");

        const use = document.createElementNS("http://www.w3.org/2000/svg", "use");
        use.setAttribute("href", `#icon-${iconName}`);
        svg.appendChild(use);

        if (element.classList && element.classList.length > 0) {
            for (const className of element.classList) {
                if (className.trim()) {
                    svg.classList.add(className);
                }
            }
        }

        svg.classList.add("wm-icon");

        element.replaceWith(svg);
    }
}
export function initDatePicker(element, triggerElement, isItinerarySearch, origindestinationinput, callback) {
    const datePicker = flatpickr(element, {
        enableTime: true,
        altInput: true,
        altFormat: "Y-m-d H:i",
        dateFormat: "Y-m-d\\TH:i",
        defaultDate: new Date(),
        positionElement: triggerElement,
        disableMobile: true,
        onClose: (selectedDates, dateStr, instance) => {
            const newDate = new Date(dateStr);
            const altFormat = instance.config.altFormat;
            onItinaryDateChange(newDate);
            const originDestination = origindestinationinput?.get("originDestination");
            if (originDestination) {
                origindestinationinput.set("originDestination", {
                    origin: originDestination.origin,
                    destination: originDestination.destination,
                });
            }
            if (selectedDates.length > 0) {
                const altValue = instance.formatDate(selectedDates[0], altFormat);
                callback(altValue);
            }
        },
        onChange(selectedDates, dateStr, instance) {
            // Blur time input to close keyboard (iphone devices)
            const timeInputs = instance.timeContainer?.querySelectorAll("input");
            for (const input of timeInputs) {
                input.blur();
            }
        },
        minDate: "today",
        plugins: [new confirmDatePlugin({ theme: "material_red", showAlways: false, confirmIcon: "" })],
    });
    triggerElement.addEventListener("click", (e) => {
        datePicker.open();
    });
}
export function generateGoogleMapsDirectionUrl(destination) {
    const travelMode = "DRIVING";

    const urlDataTravelModeAndIndexCode = {
        WALKING: "!3e2",
        BICYCLING: "!3e1",
        DRIVING: "!3e0",
        TRANSIT: "!3e3",
    };

    const googleMapDirectionBaseUrl = "https://www.google.com/maps/dir/";

    const destArr = destination;

    let directionUrl = googleMapDirectionBaseUrl;
    directionUrl += "/";
    directionUrl += `${destArr[1]},${destArr[0]}/`;

    const travelCode = urlDataTravelModeAndIndexCode[travelMode] || "";
    directionUrl += `data=!4m2!4m1${travelCode}`;

    return directionUrl;
}
export function generateWazeDirectionUrl(destination) {
    const destArr = destination;
    const wazeBaseUrl = "https://waze.com/ul?ll=";

    // Construct the Waze URL with latitude and longitude
    const wazeUrl = `${wazeBaseUrl}${destArr[1]},${destArr[0]}&navigate=yes`;

    return wazeUrl;
}
if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initIcons);
} else {
    initIcons();
}
export function updateTrafficElements(selectedDate, button, label, depDateEl) {
    if (button.classList.contains("wm-active")) {
        button.classList.remove("wm-active");
    }
    label.classList.add("wm-hidden");
    depDateEl.textContent = selectedDate;
    depDateEl.style.display = "inline";
    depDateEl.parentElement.classList.add("wm-active");

    // document.querySelector('[data-id="departNow"]').disabled = true;
}
export function resetTrafficElements(label, depDateEl) {
    label.classList.remove("wm-hidden");
    depDateEl.classList.add("wm-hidden");
    depDateEl.textContent = "";
    depDateEl.parentElement.classList.remove("wm-active");
}
export function selectRouteByIndexInPanel(index) {
    selectedRouteIndex = index;
    const directionsResultItemToSelect = document.querySelector(
        `.wm-directions--result[data-original-index="${index}"]`,
    );
    if (directionsResultItemToSelect) {
        directionsResultItemToSelect.click();
    }
}
export function getSelectedRouteIndex() {
    return selectedRouteIndex;
}
window.reinitIcons = initIcons;
